import { request, getApiUrl } from '@/utils/ajax';
import {
    ChangeRequest,
    ObjectHistoryItem,
    ChangeRequestParticipant,
    ChangeRequestParticipantInfo,
    ChangeRequestAttachment,
    OptionalId,
    BuyerOffer,
    SellerChangeRequestStatus,
    PriceCalculationResult,
    FeeConfiguration,
    ChangeRequestFilterData,
    ProjectTag,
    ChangeRequestOverview,
    ChangeRequestUserOverview,
    BuyerOptionGroupOverride,
    OptionGroupOverrideSelection,
    ChangeRequestAvailableParticipant,
} from '@/types';
import { FileData } from '@ygtechnologies/rubus-frontend-utilities';

export type ChangeRequestUpdatedAttachment = OptionalId<ChangeRequestAttachment> & {
    file?: FileData;
};

export interface CreateChangeRequestOptions {
    residentialUnitId: number;
    subject: string;
    description: string;
    internalDescription: string;

    attachments: ChangeRequestAttachment[];
}

export function createChangeRequest(changeRequest: CreateChangeRequestOptions): Promise<ChangeRequest> {
    return request(`api/change-request`, {
        method: 'POST',
        json: true,
        body: changeRequest,
    }).then((x) => x.json());
}

export function getChangeRequest(changeRequestId: number): Promise<ChangeRequest> {
    return request(`api/change-request/${changeRequestId}`).then((x) => x.json());
}

export interface SearchChangeRequestsOptions {
    projectId?: number;
    units?: number[];
    groups?: number[];
    statuses?: SellerChangeRequestStatus[];
    participants?: number[];
    onlyUnassigned?: boolean;
    onlyMine?: boolean;
    searchText?: string;
    includeArchived?: boolean;
}
export function searchChangeRequests(options: SearchChangeRequestsOptions): Promise<ChangeRequest[]> {
    return request(`api/change-request/`, { query: options }).then((x) => x.json());
}

export function getChangeRequestHistory(changeRequestId: number): Promise<ObjectHistoryItem> {
    return request(`api/change-request/${changeRequestId}/history`).then((x) => x.json());
}

export function getChangeRequestAvailableParticipants(projectId: number): Promise<ChangeRequestAvailableParticipant[]> {
    return request(`api/change-request/available-participants`, { query: { projectId } }).then((x) => x.json());
}

export function getChangeRequestParticipants(changeRequestId: number): Promise<ChangeRequestParticipantInfo[]> {
    return request(`api/change-request/${changeRequestId}/participants`).then((x) => x.json());
}

export function setChangeRequestParticipants(changeRequestId: number, participants: OptionalId<ChangeRequestParticipant>[]) {
    return request(`api/change-request/${changeRequestId}/participants`, {
        method: 'PUT',
        json: true,
        body: participants,
    });
}

export interface ChangeRequestSplitOptions {
    isOriginal: boolean;
    description: string;
    subject: string;
}
export function splitChangeRequest(
    changeRequestId: number,
    splitOptions: ChangeRequestSplitOptions[]
): Promise<ChangeRequest[]> {
    return request(`api/change-request/${changeRequestId}/split`, {
        method: 'POST',
        json: true,
        body: splitOptions,
    }).then((x) => x.json());
}

export interface ChangeRequestMergeOptions {
    changeRequestIds: number[];
    subject: string;
    description: string;
    tags: ProjectTag[];
}
export function mergeChangeRequests(mergeOptions: ChangeRequestMergeOptions): Promise<ChangeRequest> {
    return request(`api/change-request/merge`, {
        method: 'POST',
        json: true,
        body: mergeOptions,
    }).then((x) => x.json());
}

export interface ChangeRequestParticipantLine {
    id: number | null;
    price: number | null;
    description: string;
    projectFeeSetId: number;
    itemCategoryId: number | null | undefined;
    tags: ProjectTag[];
}
export interface ChangeRequestParticipantOffer {
    note: string;
    attachments: ChangeRequestUpdatedAttachment[];
    lines: ChangeRequestParticipantLine[];
    participantId?: number;
}

export function setChangeRequestParticipantOffer(
    changeRequestId: number,
    offer: ChangeRequestParticipantOffer
): Promise<ChangeRequestParticipantInfo> {
    return request(`api/change-request/${changeRequestId}/participants/current/offer`, {
        method: 'PUT',
        json: true,
        body: offer,
    }).then((x) => x.json());
}

export function setChangeRequestParticipantReadyState(changeRequestId: number, participantUserId: number, isReady: boolean) {
    return request(`api/change-request/${changeRequestId}/participants/${participantUserId}/ready`, {
        method: 'PATCH',
        json: true,
        body: isReady,
    });
}

export function getChangeRequestOffers(changeRequestId: number): Promise<BuyerOffer[]> {
    return request(`api/change-request/${changeRequestId}/buyer-offer`).then((x) => x.json());
}

export interface SendChangeRequestBuyerOfferOptions {
    note: string;
    showSubtotalsToBuyers: boolean;
}

export function sendChangeRequestBuyerOffer(changeRequestId: number, options: SendChangeRequestBuyerOfferOptions) {
    return request(`api/change-request/${changeRequestId}/buyer-offer`, {
        method: 'POST',
        json: true,
        body: options,
    });
}

export function retractChangeRequestBuyerOffer(changeRequestId: number, note: string) {
    return request(`api/change-request/${changeRequestId}/buyer-offer/retract`, {
        method: 'POST',
        json: true,
        body: {
            note,
        },
    });
}

export function closeChangeRequest(changeRequestId: number, note: string) {
    return request(`api/change-request/${changeRequestId}/close`, {
        method: 'POST',
        json: true,
        body: {
            note,
        },
    });
}

export function reopenChangeRequest(changeRequestId: number) {
    return request(`api/change-request/${changeRequestId}/reopen`, {
        method: 'POST',
        json: true,
    });
}

export function requestNewChangeRequestOffers(changeRequestId: number) {
    return request(`api/change-request/${changeRequestId}/request-new-offers`, {
        method: 'POST',
    });
}

export function calculateChangeRequestParticipantPrice(
    projectId: number,
    priceWithoutVat: number
): Promise<PriceCalculationResult> {
    return request(`api/change-request/calculate`, {
        method: 'GET',
        query: {
            projectId,
            price: priceWithoutVat,
        },
    }).then((x) => x.json());
}

export function getChangeRequestParticipantFees(changeRequestParticipantId: number): Promise<FeeConfiguration[]> {
    return request(`api/change-request-participant/${changeRequestParticipantId}/fees`).then((x) => x.json());
}

export function saveChangeRequestParticipantFees(changeRequestParticipantId: number, fees: FeeConfiguration[]) {
    return request(`api/change-request-participant/${changeRequestParticipantId}/fees`, {
        method: 'PUT',
        json: true,
        body: fees,
    });
}

export function getDownloadChangeRequestAttachmentUrl(attachmentId: number) {
    return getApiUrl() + `/api/change-request-attachment/${attachmentId}/attachment/download`;
}

export function getOpenChangeRequestAttachmentUrl(attachmentId: number) {
    return getApiUrl() + `/api/change-request-attachment/${attachmentId}/attachment/open`;
}

export function getChangeRequestAttachmentThumbnailUrl(attachmentId: number) {
    return getApiUrl() + `/api/change-request-attachment/${attachmentId}/attachment/thumbnail`;
}

export function getChangeRequestFilterData(projectId: number): Promise<ChangeRequestFilterData> {
    return request(`api/change-request/${projectId}/filter-data`, {}).then((r) => r.json());
}

export function setChangeRequestTags(changeRequestId: number, tags: OptionalId<ProjectTag>[]): Promise<ProjectTag[]> {
    return request(`api/change-request/${changeRequestId}/tags`, {
        method: 'PATCH',
        json: true,
        body: { tags },
    }).then((r) => r.json());
}

export interface SearchChangeRequestsOptions {
    projectId?: number;
    units?: number[];
    groups?: number[];
    participants?: number[];
    onlyUnassigned?: boolean;
    onlyMine?: boolean;
    searchText?: string;
}

export function searchChangeRequestsOverview(options: SearchChangeRequestsOptions): Promise<ChangeRequestOverview[]> {
    return request(`api/change-request/overview`, { query: options }).then((x) => x.json());
}
export function getChangeRequestParticipantsOverview(projectId: number): Promise<ChangeRequestUserOverview[]> {
    return request(`api/change-request/user-overview`, { query: { projectId } }).then((x) => x.json());
}

export function setChangeRequestOptionOverrides(
    changeRequestId: number,
    overrides: OptionGroupOverrideSelection[]
): Promise<BuyerOptionGroupOverride[]> {
    return request(`api/change-request/${changeRequestId}/overrides`, {
        method: 'PUT',
        json: true,
        body: overrides,
    }).then((r) => r.json());
}

export function archiveChangeRequest(changeRequestId: number, isArchived: boolean) {
    return request(`api/change-request/${changeRequestId}/archived`, { method: 'PATCH', json: true, body: { isArchived } });
}
export interface ChangeRequestAttachmentUpdateOptions {
    hideFromBuyer?: boolean | null;
    isSensitive?: boolean | null;
}

export function updateChangeRequestAttachment(
    changeRequestAttachmentId: number,
    options: ChangeRequestAttachmentUpdateOptions
): Promise<ChangeRequestAttachment> {
    return request(`api/change-request-attachment/${changeRequestAttachmentId}`, {
        method: 'PATCH',
        json: true,
        body: options,
    }).then((r) => r.json());
}

export function changeParticipantUser(participantId: number, newProfileId: number, newUserId: number) {
    return request(`api/change-request-participant/${participantId}/change-user`, {
        method: 'POST',
        json: true,
        body: {
            newProfileId,
            newUserId,
        },
    });
}

export interface ChangeRequestEdit {
    subject: string;
    internalDescription: string;
}

export function editChangeRequest(changeRequestId: number, changeRequest: ChangeRequestEdit) {
    return request(`api/change-request/${changeRequestId}`, {
        method: 'PATCH',
        json: true,
        body: changeRequest,
    });
}
